/**
 * preview-pool-groups — split the paths list into one block per allocation
 * pool so the panel reads the way the flow is actually structured: the
 * default paths first, then each rule group as its own clearly separated
 * block.
 *
 * A flat list mixed default paths and rule paths together, which hid the one
 * thing that matters when reading weights — items only ever compete for
 * traffic INSIDE their own pool, so a default path at 28% and a rule path at
 * 28% are two unrelated 28%s.
 *
 * Order follows the backend `pools` array (default first, then rule groups in
 * flow order). A pool key with no entry in that array — a payload predating
 * `pools`, or a group deleted mid-preview — is kept and sorted last rather
 * than dropped, so no path can silently vanish from the panel.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  FlowOptimizerItemPreview,
  FlowOptimizerPool,
} from '@trafficpulse/shared';

export interface PreviewPoolGroup {
  id: string;
  name: string;
  items: FlowOptimizerItemPreview[];
}

const UNGROUPED_LABEL = 'Ungrouped paths';

/**
 * Group path items by their pool key (`parentId`). Returns `null` when
 * grouping would add nothing — fewer than two pools are actually represented
 * — so the caller keeps the plain flat list.
 */
export function groupPathsByPool(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
  pools: ReadonlyArray<FlowOptimizerPool>,
): PreviewPoolGroup[] | null {
  const byPool = new Map<string, FlowOptimizerItemPreview[]>();
  for (const item of items) {
    const key = item.parentId ?? '';
    const bucket = byPool.get(key);
    if (bucket) bucket.push(item);
    else byPool.set(key, [item]);
  }
  if (byPool.size < 2) return null;

  const order = new Map(pools.map((p, i) => [p.id, i] as const));
  const nameOf = new Map(pools.map((p) => [p.id, p.name] as const));
  const groups: PreviewPoolGroup[] = [...byPool.entries()].map(([id, list]) => ({
    id,
    name: nameOf.get(id) ?? (id || UNGROUPED_LABEL),
    items: list,
  }));
  groups.sort(
    (a, b) =>
      (order.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(b.id) ?? Number.MAX_SAFE_INTEGER),
  );
  return groups;
}
